
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { ProfileInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import {
  UserRepoInterface,
  UserResponseInterface,
} from '@/services/Users/Interfaces';
import { ProfileMapInterface } from '@/services/QC-V2/chat/interfaces';
import DossierUserRepositoryInterface, { AgentInterface } from '@/services/Dossier/user/interfaces';
import DossierFeedbackRepositoryInterface, {
  AgentFeedbackInterface,
  FeedbackCategoryInterface,
} from '@/services/Dossier/feedback/interfaces';

interface AgentFeedbackCategories {
  id: number;
  name: string;
  feedbacks: AgentFeedbackInterface[];
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'dossierUserRepository',
    'dossierFeedbackRepository',
    'profileRepository',
    'userRepository',
  ],
})
export default class AgentDossier extends Vue {
  private dossierUserRepository!: DossierUserRepositoryInterface;
  private dossierFeedbackRepository!: DossierFeedbackRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private userRepository!: UserRepoInterface;

  private loading: boolean = true;

  private userId!: string;
  private agent!: AgentInterface;
  private user!: UserResponseInterface;
  private profile!: ProfileInterface;
  private feedbackCategories: FeedbackCategoryInterface[] = [];
  private agentFeedbackCategories: AgentFeedbackCategories[] = [];
  private profilesMap: ProfileMapInterface = {};

  private colorsMap: { [key: string]: string } = {
    'On Training': '#DBEDDB',
    '1:1 Team Managers': '#D3E5EF',
    'QC Feedback': '#E8DEEE',
  };

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      if (this.$route.params.userId.includes('@')) {
        const [user] = await this.userRepository.getUsersByEmails([this.$route.params.userId]);
        this.$route.params.userId = user.id;
        await this.$router.replace({ path: `/dossiers/${user.id}`, params: { userId: user.id } });
      }

      this.userId = this.$route.params.userId;
      this.agent = await this.dossierUserRepository.getUser(this.userId);
      this.user = await this.userRepository.getUser(this.userId);
      this.profile = await this.profileRepository.getOneByUserId(this.userId);
      this.feedbackCategories = await this.dossierFeedbackRepository.getFeedbackCategories();
      this.agentFeedbackCategories = this.feedbackCategories.map((category) => ({ ...category, feedbacks: [] }));
      this.assignFeedbacksToCategories();
      await this.fetchUsersProfiles();
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e?.response?.data?.message || e.message);
    }
  }

  private assignFeedbacksToCategories(): void {
    this.agent.receivedFeedbacks.forEach((feedback) => {
      const categoryIndex = this.agentFeedbackCategories.findIndex((category) => category.id === feedback.category.id);
      if (categoryIndex === -1) {
        return;
      }

      this.agentFeedbackCategories[categoryIndex].feedbacks.push(feedback);
    });

    this.agentFeedbackCategories.forEach((_, index) => {
      this.agentFeedbackCategories[index].feedbacks = this.agentFeedbackCategories[index].feedbacks
        .sort((feedbackA, feedbackB) =>
          new Date(feedbackB.forMonth).getTime() - new Date(feedbackA.forMonth).getTime(),
        );
    });
  }

  private async fetchUsersProfiles(): Promise<void> {
    const ids: string[] = [];
    if (this.agent.receivedFeedbacks.length) {
      this.agent.receivedFeedbacks.forEach((feedback) => {
        if (ids.includes(feedback.from.id)) {
          return;
        }
        ids.push(feedback.from.id);
      });
    }

    if (this.agent.membership && !ids.includes(this.agent.membership.team.manager.id)) {
      ids.push(this.agent.membership.team.manager.id);
    }

    if (!ids.length) {
      return;
    }

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private getTimeServed(): string {
    if (!this.profile.companyDetails?.firstDay) {
      return 'N/A';
    }

    const startDate = new Date(this.profile.companyDetails.firstDay);
    const endDate = new Date();

    let months;
    months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months -= startDate.getMonth();
    months += endDate.getMonth();

    months = months <= 0 ? 0 : months;
    const years = Math.floor(months / 12);

    return `${years} Years and ${months % 12} Months`;
  }

  private async addFeedback(category: FeedbackCategoryInterface): Promise<void> {
    await this.$router.push({
      name: 'create-agent-feedback',
      params: { userId: this.userId, categoryId: String(category.id) },
      query: { category: String(category.id) },
    });
  }

  private async viewFeedback(feedbackId: number): Promise<void> {
    await this.$router.push({ name: 'view-feedback', params: { feedbackId: String(feedbackId) } });
  }
}
